import {
  isLiveTagMappingGrammarPreview,
  parseTagGrammarPairs,
  parseTagMappingLinkExcludes,
  type TagMappingGrammarPair,
  type TagMappingLinkExclude
} from '@axilon-apps/domains-ontology-ui-state'

export function excludeStoredLink(
  grammars: readonly TagMappingGrammarPair[],
  link: { aName: string; bName: string; grammarId?: string }
): TagMappingGrammarPair[] {
  const aName = link.aName.trim()
  const bName = link.bName.trim()
  if (!aName || !bName) return [...grammars]
  return grammars.map((grammar) => {
    if (link.grammarId && grammar.id !== link.grammarId) return grammar
    const exclude = parseTagMappingLinkExcludes([...(grammar.exclude ?? []), { aName, bName }])
    return { ...grammar, exclude }
  })
}

export function isStoredLinkExcluded(
  grammars: readonly TagMappingGrammarPair[],
  link: { aName: string; bName: string; grammarId?: string }
): boolean {
  return grammars.some((grammar) => {
    if (link.grammarId && grammar.id !== link.grammarId) return false
    return (grammar.exclude ?? []).some(
      (row) =>
        row.aName.trim().toUpperCase() === link.aName.trim().toUpperCase() &&
        row.bName.trim().toUpperCase() === link.bName.trim().toUpperCase()
    )
  })
}

export function parseStoredGrammars(raw: string | undefined): TagMappingGrammarPair[] {
  if (!raw?.trim()) return []
  try {
    return parseTagGrammarPairs(JSON.parse(raw))
  } catch {
    return []
  }
}

export function serializeStoredGrammars(grammars: readonly TagMappingGrammarPair[]): string {
  return JSON.stringify(grammars)
}

/**
 * Keep pairs that still produce links under the current preview. Leave the
 * list unchanged when preview slices are missing or not aligned (finish stub
 * and in-flight preview both ship `grammars: []`).
 */
export function keepLinkedGrammars(
  grammars: readonly TagMappingGrammarPair[],
  previewGrammars:
    | ReadonlyArray<{
        id: string
        matchCount: number
        a?: { matched?: number }
        b?: { matched?: number }
      }>
    | undefined
): TagMappingGrammarPair[] {
  if (!previewGrammars?.length) return [...grammars]
  const byId = new Map(previewGrammars.map((slice) => [slice.id, slice]))
  if (grammars.some((grammar) => !byId.has(grammar.id))) return [...grammars]
  return grammars.filter((grammar) => {
    const slice = byId.get(grammar.id)
    return slice != null && isLiveTagMappingGrammarPreview(slice)
  })
}

export const MANUAL_LINK_GRAMMAR_ID = 'manual'

export function parseStoredManualLinks(raw: string | undefined): TagMappingLinkExclude[] {
  return parseTagMappingLinkExcludes(raw?.trim() ? safeParseArray(raw) : [])
}

export function serializeStoredManualLinks(links: readonly TagMappingLinkExclude[]): string {
  return JSON.stringify(links)
}

export function addManualLinks(
  links: readonly TagMappingLinkExclude[],
  added: readonly TagMappingLinkExclude[]
): TagMappingLinkExclude[] {
  return parseTagMappingLinkExcludes([...links, ...added])
}

export function removeManualLink(
  links: readonly TagMappingLinkExclude[],
  link: { aName: string; bName: string }
): TagMappingLinkExclude[] {
  const a = link.aName.trim().toUpperCase()
  const b = link.bName.trim().toUpperCase()
  return links.filter(
    (row) => row.aName.trim().toUpperCase() !== a || row.bName.trim().toUpperCase() !== b
  )
}

export function isManualLink(
  links: readonly TagMappingLinkExclude[],
  link: { aName: string; bName: string }
): boolean {
  return links.some(
    (row) =>
      row.aName.trim().toUpperCase() === link.aName.trim().toUpperCase() &&
      row.bName.trim().toUpperCase() === link.bName.trim().toUpperCase()
  )
}

function safeParseArray(raw: string): unknown[] {
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function tagMappingIsReady(fields: Record<string, string> | undefined): boolean {
  if (!fields?.mappingTypeId?.trim()) return false
  if (parseStoredGrammars(fields.grammars).length > 0) return true
  if (parseStoredManualLinks(fields.manualLinks).length > 0) return true
  return Boolean(fields.aGrammar?.trim() && fields.bGrammar?.trim())
}
